<script setup>
import { ref } from 'vue'
import { Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions } from '@headlessui/vue'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/vue/20/solid'

const emits = defineEmits(['update:modelValue']);

const props = defineProps({
    options: {
        type: Array,
        default: () => [],
    },

    label: {
        type: String,
        required: true,
    },

    modelValue: {
        default: null,
    },

    id: {
        type: String,
        required: true,
    }
})

const selected = ref(props.modelValue)
</script>

<template>
    <Listbox as="div" :modelValue="modelValue" @update:modelValue="value => emits('update:modelValue', value)" by="id">
        <ListboxLabel class="block text-base font-bi-text-medium text-bi-dark-green">{{ label }}</ListboxLabel>
        <div class="relative mt-1">
            <ListboxButton class="relative w-full cursor-default rounded-md text-base py-3 pl-3 pr-10 text-left border-bi-warm-gray border-2 border-solid focus:outline-none shadow-none focus:border-bi-accent-green ring-0 bg-bi-white
            ">
                <span class="block truncate text-bi-dark-green font-bi-text-medium" v-if="modelValue">{{
                    modelValue?.name
                }}</span>
                <span class="block truncate text-bi-gray-60 font-bi-text" v-else>
                    {{ $t('form.please.select') }} </span>
                <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronUpDownIcon class="h-5 w-5 text-bi-warm-gray" aria-hidden="true" />
                </span>
            </ListboxButton>

            <transition leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100"
                leave-to-class="opacity-0">
                <ListboxOptions v-if="options?.length"
                    class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-bi-dark-green ring-opacity-5 focus:outline-none sm:text-sm">
                    <ListboxOption as="template" v-for="item in options" :key="item.id" :value="item"
                        v-slot="{ active, selected }">
                        <li
                            :class="[active ? 'text-bi-white bg-bi-dark-green' : 'text-bi-dark-green', 'relative cursor-default select-none py-2 pl-8 pr-4']">
                            <span :class="[selected ? 'font-semibold' : 'font-normal', 'block']">
                                {{ item.name }}
                            </span>

                            <span v-if="selected"
                                :class="[active ? 'text-white' : 'text-bi-accent-green', 'absolute inset-y-0 left-0 flex items-center pl-1.5']">
                                <CheckIcon class="h-5 w-5" aria-hidden="true" />
                            </span>
                        </li>
                    </ListboxOption>
                </ListboxOptions>
                <ListboxOptions v-else
                    class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-bi-dark-green ring-opacity-5 focus:outline-none sm:text-sm">
                    <div class="text-bi-dark-green text-center py-2">
                        {{ $t('form.no.options') }}
                    </div>
                </ListboxOptions>
            </transition>
        </div>
    </Listbox>
</template>
